import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO } from '../../utils/seo';
import ConsultModal from './components/ConsultModal';
import DemoBookingModal from '../../components/feature/DemoBookingModal';

export default function SolutionsPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  useSEO({
    title: '解决方案 - 智研云 | 研发费用管理 | 高新认定 | 税收优惠',
    description: '智研云为不同规模企业提供专业的研发费用管理解决方案，涵盖研发项目管理、费用归集核算、高新技术企业认定、税收优惠申报等全流程服务，助力企业降本增效。',
    keywords: '解决方案,研发费用管理,高新认定,税收优惠,项目管理,费用核算',
    canonical: '/solutions',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: '解决方案 - 智研云',
      url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/solutions`,
      description: '智研云为不同规模企业提供专业的研发费用管理解决方案',
      mainEntity: {
        '@type': 'Service',
        name: '研发费用管理解决方案',
        provider: {
          '@type': 'Organization',
          name: '智研云'
        },
        serviceType: '研发费用管理',
        areaServed: 'CN',
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: '解决方案',
          itemListElement: [
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: '研发项目管理',
                description: '全生命周期项目管理'
              }
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: '费用归集核算',
                description: '智能数据归集与精准核算'
              }
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: '高新认定支持',
                description: '高新技术企业认定全流程支持'
              }
            }
          ]
        }
      }
    }
  });

  const solutions = [
    {
      icon: 'ri-building-line',
      title: '高新技术企业',
      subtitle: '助力企业顺利通过高新认定',
      description: '为申请或已获得高新技术企业资质的公司提供全方位研发费用管理支持，确保研发活动符合认定标准。',
      features: [
        '研发项目自动分类归档',
        '高新认定材料一键生成',
        '研发费用占比实时监控',
        '知识产权管理与追踪'
      ],
      benefits: '提高认定通过率，降低管理成本60%',
      image: 'https://readdy.ai/api/search-image?query=Modern%20high-tech%20enterprise%20office%20building%20with%20glass%20facade%20and%20innovative%20technology%20elements%20clean%20professional%20atmosphere%20with%20simple%20light%20background%20business%20success%20concept&width=600&height=400&seq=solution-hightech&orientation=landscape'
    },
    {
      icon: 'ri-flask-line',
      title: '研发密集型企业',
      subtitle: '精细化管理多项目研发活动',
      description: '针对拥有多个并行研发项目的企业，提供精细化的项目管理和费用追踪解决方案。',
      features: [
        '多项目并行管理',
        '跨部门协作支持',
        '资源分配优化建议',
        '项目成本精准核算'
      ],
      benefits: '提升项目管理效率80%，降低人力成本50%',
      image: 'https://readdy.ai/api/search-image?query=Scientific%20research%20laboratory%20with%20modern%20equipment%20and%20researchers%20working%20on%20innovative%20projects%20clean%20bright%20environment%20with%20simple%20white%20background%20technology%20innovation%20atmosphere&width=600&height=400&seq=solution-research&orientation=landscape'
    },
    {
      icon: 'ri-line-chart-line',
      title: '拟上市企业',
      subtitle: 'IPO审计资料完整准备',
      description: '为准备上市的企业提供符合IPO审计要求的研发费用管理和资料归档服务。',
      features: [
        'IPO审计资料自动整理',
        '历史数据完整追溯',
        '合规性全面检查',
        '审计问询快速响应'
      ],
      benefits: '缩短IPO准备周期3个月，审计通过率100%',
      image: 'https://readdy.ai/api/search-image?query=Stock%20market%20trading%20floor%20with%20digital%20screens%20showing%20financial%20data%20and%20charts%20professional%20business%20environment%20with%20simple%20clean%20background%20IPO%20success%20concept&width=600&height=400&seq=solution-ipo&orientation=landscape'
    },
    {
      icon: 'ri-calculator-line',
      title: '税务筹划企业',
      subtitle: '最大化研发费用加计扣除',
      description: '帮助企业充分利用研发费用加计扣除政策，合理合规地降低税负。',
      features: [
        '加计扣除自动测算',
        '政策变化实时提醒',
        '留存备查资料完整归档',
        '税务风险提前预警'
      ],
      benefits: '平均为企业节省税费200万+/年',
      image: 'https://readdy.ai/api/search-image?query=Professional%20tax%20planning%20office%20with%20financial%20documents%20and%20calculator%20on%20desk%20clean%20organized%20workspace%20with%20simple%20light%20background%20business%20efficiency%20concept&width=600&height=400&seq=solution-tax&orientation=landscape'
    },
    {
      icon: 'ri-group-line',
      title: '集团化企业',
      subtitle: '多法人主体统一管理',
      description: '为拥有多个法人主体的集团企业提供统一的研发费用管理平台，实现数据互通和集中管控。',
      features: [
        '多法人主体统一管理',
        '集团数据汇总分析',
        '跨公司项目协同',
        '分级权限精细控制'
      ],
      benefits: '实现集团化管理，提升协同效率70%',
      image: 'https://readdy.ai/api/search-image?query=Corporate%20group%20headquarters%20building%20with%20multiple%20connected%20structures%20representing%20unified%20management%20modern%20architecture%20with%20simple%20clean%20background%20business%20integration%20concept&width=600&height=400&seq=solution-group&orientation=landscape'
    },
    {
      icon: 'ri-service-line',
      title: '财税服务机构',
      subtitle: '为客户提供专业服务',
      description: '为会计师事务所、税务师事务所等服务机构提供专业工具，提升客户服务质量和效率。',
      features: [
        '多客户统一管理',
        '服务流程标准化',
        '客户数据安全隔离',
        '专业报告自动生成'
      ],
      benefits: '服务客户数量提升3倍，客户满意度95%+',
      image: 'https://readdy.ai/api/search-image?query=Professional%20financial%20consulting%20office%20with%20advisors%20meeting%20clients%20modern%20business%20environment%20with%20simple%20clean%20background%20professional%20service%20excellence%20concept&width=600&height=400&seq=solution-service&orientation=landscape'
    }
  ];

  return (
    <>
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-50 via-teal-50 to-white py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=Business%20solution%20concept%20with%20interconnected%20puzzle%20pieces%20and%20strategic%20planning%20elements%20in%20soft%20blue%20and%20teal%20gradient%20modern%20minimalist%20design&width=1920&height=600&seq=solutions-hero-bg&orientation=landscape')] opacity-5 bg-cover bg-center"></div>
          
          <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
            <div className="text-center max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-5 py-2 rounded-full text-sm font-semibold mb-6">
                <i className="ri-lightbulb-flash-fill"></i>
                <span>量身定制的解决方案</span>
              </div>
              
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                为不同企业提供<br />
                <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">专业解决方案</span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                无论您是高新技术企业、拟上市公司还是财税服务机构<br />
                我们都有针对性的解决方案满足您的需求
              </p>

              <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
                <div className="flex items-center gap-2 text-gray-700">
                  <i className="ri-checkbox-circle-fill text-blue-600 text-xl"></i>
                  <span>6大行业解决方案</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <i className="ri-checkbox-circle-fill text-blue-600 text-xl"></i>
                  <span>1000+企业信赖</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <i className="ri-checkbox-circle-fill text-blue-600 text-xl"></i>
                  <span>98%客户满意度</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Solutions Grid */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="space-y-16">
              {solutions.map((solution, index) => (
                <div 
                  key={index}
                  className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 items-center`}
                >
                  <div className="flex-1">
                    <div className="inline-flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-blue-500 to-teal-500 rounded-xl">
                        <i className={`${solution.icon} text-2xl text-white`}></i>
                      </div>
                      <div>
                        <h3 className="text-3xl font-bold text-gray-900">{solution.title}</h3>
                        <p className="text-teal-600 font-medium">{solution.subtitle}</p>
                      </div>
                    </div>

                    <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                      {solution.description}
                    </p>

                    <div className="space-y-3 mb-6">
                      {solution.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <i className="ri-check-double-line text-teal-600 text-xl mt-0.5 flex-shrink-0"></i>
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl p-5 border-l-4 border-teal-500">
                      <div className="flex items-start gap-3">
                        <i className="ri-trophy-fill text-teal-600 text-2xl flex-shrink-0"></i>
                        <div>
                          <p className="font-semibold text-gray-900 mb-1">核心价值</p>
                          <p className="text-gray-700">{solution.benefits}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex-1">
                    <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
                      <img 
                        src={solution.image}
                        alt={solution.title}
                        className="w-full h-[400px] object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-blue-600 to-teal-600 relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=Abstract%20business%20success%20concept%20with%20upward%20arrows%20and%20growth%20charts%20in%20white%20and%20light%20blue%20modern%20minimalist%20background&width=1920&height=400&seq=solutions-cta-bg&orientation=landscape')] opacity-10 bg-cover bg-center"></div>
          
          <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center relative z-10">
            <h2 className="text-4xl font-bold text-white mb-6">
              找到适合您的解决方案
            </h2>
            <p className="text-xl text-white/90 mb-8">
              联系我们的专业顾问，获取定制化方案建议
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <button 
                onClick={() => setIsModalOpen(true)}
                className="bg-white text-blue-600 px-8 py-4 rounded-full font-semibold hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                咨询顾问
              </button>
              <button 
                onClick={() => setIsDemoModalOpen(true)}
                className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-full font-semibold hover:bg-white/20 transition-all border-2 border-white/30 whitespace-nowrap cursor-pointer"
              >
                预约演示
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      
      {/* 咨询顾问弹窗 */}
      <ConsultModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      
      {/* 预约演示弹窗 */}
      <DemoBookingModal isOpen={isDemoModalOpen} onClose={() => setIsDemoModalOpen(false)} />
    </>
  );
}
